import React, { useState, useEffect, useRef, useCallback } from 'react';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
import '../styles.scss';

// Use ag-Grid standard filter types
export type FilterType = 'greaterThan' | 'lessThan' | 'inRange';

export interface DateFilterValue {
  type: FilterType;
  startDate: Date;
  startTime: string;
  endDate?: Date;
  endTime?: string;
}

interface DateFilterComponentProps {
  onApply: (filterValue: DateFilterValue | null) => void;
  onReset?: () => void;
  onClose?: () => void;
  currentFilter?: DateFilterValue | null;
  defaultFilterType?: FilterType;
}

// Helper functions for default values
const getDefaultDateTime = (): Date => {
  const date = new Date();
  date.setHours(0, 1, 0, 0);
  return date;
};

const getDefaultTime = (): string => '00:01';

const DateFilterComponent: React.FC<DateFilterComponentProps> = ({
  onApply,
  onReset,
  onClose,
  currentFilter,
  defaultFilterType = 'greaterThan',
}) => {
  const componentRef = useRef<HTMLDivElement>(null);

  // Initialize state with current filter or defaults
  const [filterType, setFilterType] = useState<FilterType>(
    currentFilter?.type || defaultFilterType
  );

  const [startDate, setStartDate] = useState<Date>(
    currentFilter?.startDate || getDefaultDateTime()
  );

  const [startTime, setStartTime] = useState<string>(
    currentFilter?.startTime || getDefaultTime()
  );

  const [endDate, setEndDate] = useState<Date>(
    currentFilter?.endDate || getDefaultDateTime()
  );

  const [endTime, setEndTime] = useState<string>(
    currentFilter?.endTime || getDefaultTime()
  );

  // Track if filter has been applied
  const [hasAppliedFilter, setHasAppliedFilter] = useState<boolean>(
    !!currentFilter
  );

  // Track temporary changes (before Apply is clicked)
  const [tempChanges, setTempChanges] = useState({
    filterType: currentFilter?.type || defaultFilterType,
    startDate: currentFilter?.startDate || getDefaultDateTime(),
    startTime: currentFilter?.startTime || getDefaultTime(),
    endDate: currentFilter?.endDate || getDefaultDateTime(),
    endTime: currentFilter?.endTime || getDefaultTime(),
  });

  // Sync state when currentFilter prop changes (e.g., when reopening the filter)
  useEffect(() => {
    const defaultDate = getDefaultDateTime();
    const defaultTime = getDefaultTime();

    const hasExistingFilter = !!currentFilter;

    if (hasExistingFilter) {
      const endDateValue = currentFilter.endDate || defaultDate;
      const endTimeValue = currentFilter.endTime || defaultTime;

      setFilterType(currentFilter.type);
      setStartDate(currentFilter.startDate);
      setStartTime(currentFilter.startTime);
      setEndDate(endDateValue);
      setEndTime(endTimeValue);
      setHasAppliedFilter(true);
      setTempChanges({
        filterType: currentFilter.type,
        startDate: currentFilter.startDate,
        startTime: currentFilter.startTime,
        endDate: endDateValue,
        endTime: endTimeValue,
      });
    } else {
      // Reset to defaults
      setFilterType(defaultFilterType);
      setStartDate(defaultDate);
      setStartTime(defaultTime);
      setEndDate(defaultDate);
      setEndTime(defaultTime);
      setHasAppliedFilter(false);
      setTempChanges({
        filterType: defaultFilterType,
        startDate: defaultDate,
        startTime: defaultTime,
        endDate: defaultDate,
        endTime: defaultTime,
      });
    }
  }, [currentFilter, defaultFilterType]);

  // Helper function to compare dates (ignoring time)
  const areDatesEqual = (date1: Date, date2: Date): boolean => {
    return date1.toDateString() === date2.toDateString();
  };

  // Check if current values match defaults
  const isUsingDefaultValues = useCallback((): boolean => {
    const defaultDate = getDefaultDateTime();
    const defaultTime = getDefaultTime();

    const isDefaultFilterType = filterType === defaultFilterType;
    const isDefaultStartDate = areDatesEqual(startDate, defaultDate);
    const isDefaultStartTime = startTime === defaultTime;

    return isDefaultFilterType && isDefaultStartDate && isDefaultStartTime;
  }, [filterType, startDate, startTime, defaultFilterType]);

  // Check if current values match saved values
  const doesCurrentMatchSaved = useCallback((): boolean => {
    const filterTypeMatches = filterType === tempChanges.filterType;

    const startDateMatches = areDatesEqual(startDate, tempChanges.startDate);
    const startTimeMatches = startTime === tempChanges.startTime;
    const startValuesMatch = startDateMatches && startTimeMatches;

    const isBetweenMode = filterType === 'inRange';
    const endDateMatches = areDatesEqual(endDate, tempChanges.endDate);
    const endTimeMatches = endTime === tempChanges.endTime;
    const endValuesMatch = endDateMatches && endTimeMatches;
    const endValuesMatchOrNotRequired = !isBetweenMode || endValuesMatch;

    return filterTypeMatches && startValuesMatch && endValuesMatchOrNotRequired;
  }, [filterType, startDate, startTime, endDate, endTime, tempChanges]);

  // Check if there are unsaved changes
  const hasChanges = useCallback((): boolean => {
    return hasAppliedFilter
      ? !doesCurrentMatchSaved()
      : !isUsingDefaultValues();
  }, [hasAppliedFilter, isUsingDefaultValues, doesCurrentMatchSaved]);

  // Helper function to revert to the last saved state
  const revertToSavedState = useCallback(() => {
    setFilterType(tempChanges.filterType);
    setStartDate(tempChanges.startDate);
    setStartTime(tempChanges.startTime);
    setEndDate(tempChanges.endDate);
    setEndTime(tempChanges.endTime);
  }, [tempChanges]);

  // Handle clicking outside the component
  useEffect(() => {
    const isProtectedElement = (target: HTMLElement): boolean => {
      // Check if click is on flatpickr calendar or input
      // stopPropagation prevents most flatpickr clicks from reaching here anyway
      if (
        target.closest('.flatpickr-calendar') ||
        target.closest('.flatpickr-input')
      ) {
        return true;
      }

      // Check for ag-Grid elements
      return !!target.closest('.ag-menu') || !!target.closest('.ag-popup');
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (isProtectedElement(target)) {
        return;
      }

      // Delay to allow Flatpickr to process clicks on arrows/time controls
      setTimeout(() => {
        const isClickOutsideComponent =
          !componentRef.current?.contains(target) &&
          !isProtectedElement(target);

        if (isClickOutsideComponent) {
          if (hasChanges()) {
            revertToSavedState();
          }
          onClose?.();
        }
      }, 50);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hasChanges, onClose, revertToSavedState]);

  // === Event Handlers ===

  const handleFilterTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterType(e.target.value as FilterType);
  };

  const handleCancel = () => {
    revertToSavedState();
    onClose?.();
  };

  const handleReset = () => {
    const defaults = {
      filterType: defaultFilterType,
      date: getDefaultDateTime(),
      time: getDefaultTime(),
    };

    setFilterType(defaults.filterType);
    setStartDate(defaults.date);
    setStartTime(defaults.time);
    setEndDate(defaults.date);
    setEndTime(defaults.time);
    setTempChanges({
      filterType: defaults.filterType,
      startDate: defaults.date,
      startTime: defaults.time,
      endDate: defaults.date,
      endTime: defaults.time,
    });
    setHasAppliedFilter(false);

    // Clear the filter WITHOUT triggering data refresh
    if (onReset) {
      onReset();
    }
    onClose?.();
  };

  const handleApply = () => {
    const isBetweenFilter = filterType === 'inRange';
    const filterValue: DateFilterValue = {
      type: filterType,
      startDate,
      startTime,
      ...(isBetweenFilter && { endDate, endTime }),
    };

    setTempChanges({ filterType, startDate, startTime, endDate, endTime });
    setHasAppliedFilter(true);
    onApply(filterValue);
    onClose?.();
  };

  // Stable callback references for flatpickr onChange handlers
  const handleStartDateChange = useCallback((dates: Date[]) => {
    setStartDate(dates[0] || getDefaultDateTime());
  }, []);

  const handleStartTimeChange = useCallback((_: Date[], timeStr: string) => {
    setStartTime(timeStr);
  }, []);

  const handleEndDateChange = useCallback((dates: Date[]) => {
    setEndDate(dates[0] || getDefaultDateTime());
  }, []);

  const handleEndTimeChange = useCallback((_: Date[], timeStr: string) => {
    setEndTime(timeStr);
  }, []);

  // Helper to format date as YYYY-MM-DD
  const formatDateForDisplay = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Factory function to create unique memoized Flatpickr options
  const createPickerOptions = useCallback(
    (isTimePicker: boolean) => ({
      ...(isTimePicker && {
        enableTime: true,
        noCalendar: true,
        time_24hr: true,
      }),
      dateFormat: isTimePicker ? 'H:i' : 'Y-m-d',
      allowInput: false,
      clickOpens: true,
      onReady: (_: any, __: any, fp: any) => {
        if (fp?.calendarContainer) {
          fp.calendarContainer.classList.add('ag-custom-component-popup');
          fp.calendarContainer.addEventListener('mousedown', (e: Event) => {
            e.stopPropagation();
          });
        }
      },
    }),
    []
  );

  // Memoized options - separate instances to prevent sharing
  const [startDateOpts, startTimeOpts, endDateOpts, endTimeOpts] =
    React.useMemo(
      () => [
        createPickerOptions(false),
        createPickerOptions(true),
        createPickerOptions(false),
        createPickerOptions(true),
      ],
      [createPickerOptions]
    );

  return (
    <div className="date-filter-component" ref={componentRef}>
      <div className="filter-content">
        {/* Filter Type Selector */}
        <div className="filter-row">
          <select
            id="filter-type"
            value={filterType}
            onChange={handleFilterTypeChange}
            className="filter-select"
          >
            <option value="lessThan">Before</option>
            <option value="greaterThan">After</option>
            <option value="inRange">Between</option>
          </select>
        </div>

        {/* Start/Single Date-Time Section */}
        <div className="datetime-section">
          <div className="datetime-row">
            <div className="date-input">
              <Flatpickr
                value={formatDateForDisplay(startDate)}
                onChange={handleStartDateChange}
                options={startDateOpts}
                className="flatpickr-input"
                placeholder="YYYY-MM-DD"
              />
            </div>
            <div className="time-input">
              <Flatpickr
                value={startTime}
                onChange={handleStartTimeChange}
                options={startTimeOpts}
                className="flatpickr-input"
                placeholder="HH:MM"
              />
            </div>
          </div>
        </div>

        {/* End Date-Time Section (only for 'inRange' mode) */}
        {filterType === 'inRange' && (
          <div className="datetime-section">
            <div className="datetime-row">
              <div className="date-input">
                <Flatpickr
                  value={formatDateForDisplay(endDate)}
                  onChange={handleEndDateChange}
                  options={endDateOpts}
                  className="flatpickr-input"
                  placeholder="YYYY-MM-DD"
                />
              </div>
              <div className="time-input">
                <Flatpickr
                  value={endTime}
                  onChange={handleEndTimeChange}
                  options={endTimeOpts}
                  className="flatpickr-input"
                  placeholder="HH:MM"
                />
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="button-row">
          <button className="btn btn-cancel" onClick={handleCancel}>
            Cancel
          </button>

          <button
            className="btn btn-reset"
            onClick={handleReset}
            disabled={!hasAppliedFilter && isUsingDefaultValues()}
            title={
              !hasAppliedFilter && isUsingDefaultValues()
                ? 'No filter to reset'
                : 'Reset to defaults'
            }
          >
            Reset
          </button>

          <button
            className="btn btn-apply"
            onClick={handleApply}
            title="Apply filter"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateFilterComponent;
